import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Search, Clock, BookOpen, Loader2, PlayCircle } from "lucide-react";

export default function Courses() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [courses, setCourses] = useState(null);
  const [category, setCategory] = useState("All");
  const [q, setQ] = useState("");

  const load = async () => {
    const { data } = await api.get("/courses");
    setCourses(data);
  };
  useEffect(() => {
    load();
  }, []);

  const categories = ["All", ...Array.from(new Set((courses || []).map((c) => c.category)))];
  const filtered = (courses || []).filter(
    (c) =>
      (category === "All" || c.category === category) &&
      (c.title.toLowerCase().includes(q.toLowerCase()) ||
        c.description.toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="mx-auto max-w-7xl px-4 sm:px-6 py-10" data-testid="course-catalog">
        <div className="flex flex-col gap-2 mb-8">
          <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">{t("courses.eyebrow")}</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-900">
            {t("courses.title")}
          </h1>
          <p className="text-slate-500 max-w-2xl">
            {t("courses.subtitle")}
          </p>
        </div>

        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-8">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              data-testid="course-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("courses.searchPlaceholder")}
              className="pl-9 bg-white"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                data-testid={`category-${cat.toLowerCase()}`}
                onClick={() => setCategory(cat)}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  category === cat
                    ? "bg-slate-900 text-white"
                    : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-100"
                }`}
              >
                {cat === "All" ? t("courses.all") : cat}
              </button>
            ))}
          </div>
        </div>

        {courses === null ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((c) => (
              <Card
                key={c.course_id}
                data-testid="course-card"
                onClick={() => navigate(`/courses/${c.course_id}`)}
                className="group cursor-pointer overflow-hidden border-slate-200 transition-all hover:shadow-lg hover:-translate-y-1"
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={c.thumbnail}
                    alt={c.title}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <Badge className="absolute top-3 left-3 bg-white/90 text-slate-800 hover:bg-white">
                    {c.category}
                  </Badge>
                  {c.enrolled && (
                    <Badge className="absolute top-3 right-3 bg-emerald-600">{t("courses.enrolled")}</Badge>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="font-heading text-lg font-bold text-slate-900 leading-snug">{c.title}</h3>
                  <p className="mt-1.5 text-sm text-slate-500 line-clamp-2">{c.description}</p>
                  <div className="mt-4 flex items-center gap-4 text-xs text-slate-400">
                    <span className="flex items-center gap-1"><BookOpen className="h-3.5 w-3.5" /> {c.lesson_count} {t("common.lessons")}</span>
                    <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {c.level}</span>
                  </div>
                  {c.enrolled ? (
                    <div className="mt-4">
                      <div className="flex justify-between text-xs mb-1.5">
                        <span className="text-slate-500">{t("courses.progress")}</span>
                        <span className="font-semibold text-slate-700">{c.progress}%</span>
                      </div>
                      <Progress value={c.progress} className="h-1.5" />
                    </div>
                  ) : (
                    <Button
                      variant="ghost"
                      className="mt-4 w-full justify-start px-0 text-blue-600 hover:text-blue-700 hover:bg-transparent"
                    >
                      <PlayCircle className="mr-1.5 h-4 w-4" /> {t("courses.viewCourse")}
                    </Button>
                  )}
                </div>
              </Card>
            ))}
            {filtered.length === 0 && (
              <p className="col-span-full text-center text-slate-400 py-16">{t("courses.noMatch")}</p>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
